import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Clock, Mail } from 'lucide-react';
import axios from 'axios';
import GoogleSignInButton from '@/components/GoogleSignInButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parse the /login error payload to a friendly notice.
// Backend now returns structured detail objects for lockout and unverified email.
const parseLoginError = (error) => {
  const detail = error?.response?.data?.detail;
  const status = error?.response?.status;

  if (status === 429 && detail && typeof detail === 'object' && detail.locked_out) {
    return {
      type: 'locked_out',
      minutes: detail.retry_after_minutes || 15,
      message: detail.message || 'Too many attempts. Try again shortly.',
    };
  }
  if (status === 403 && detail && typeof detail === 'object' && detail.requires_verification) {
    return {
      type: 'unverified',
      email: detail.email,
      message: detail.message || 'Please verify your email address before signing in.',
    };
  }
  return {
    type: 'error',
    message: (typeof detail === 'string' ? detail : detail?.message) || 'Failed to sign in',
  };
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'locked_out' | 'unverified' | 'error', ... }
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotice(null);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        toast.info('Verification code sent to your email');
        navigate('/verify-2fa', {
          state: {
            email: formData.email,
            tempToken: response.data.temp_token
          }
        });
        return;
      }

      // Normal login flow - store token and user in context
      const { access_token, user } = response.data;
      login(access_token, user);
      toast.success('Welcome back!');

      // Redirect based on user status
      if (!user.consent_accepted) {
        navigate('/consent');
      } else if (!user.current_phase) {
        navigate('/assessment');
      } else if (!['active', 'trialing'].includes(user.subscription_status)) {
        navigate('/checkout');
      } else {
        navigate('/sanctuary');
      }
    } catch (error) {
      const parsed = parseLoginError(error);
      setNotice(parsed);
      if (parsed.type === 'error') {
        toast.error(parsed.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1
            className="text-4xl md:text-5xl font-medium tracking-tight text-stone-800 mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="login-heading"
          >
            Sage is waiting for you
          </h1>
          <p className="text-stone-600">No judgment. Just pick up where you left off.</p>
        </div>

        <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8">
          {notice?.type === 'locked_out' && (
            <div
              className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900"
              data-testid="login-lockout-notice"
              role="alert"
            >
              <Clock className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-medium">Too many attempts</p>
                <p className="text-amber-800">
                  Please try again in{' '}
                  <span data-testid="login-lockout-minutes" className="font-semibold">
                    {notice.minutes}
                  </span>{' '}
                  minute{notice.minutes === 1 ? '' : 's'}.
                </p>
              </div>
            </div>
          )}

          {notice?.type === 'unverified' && (
            <div
              className="mb-6 flex items-start gap-3 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-800"
              data-testid="login-unverified-notice"
              role="alert"
            >
              <Mail className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-medium">Confirm your email first</p>
                <p className="text-stone-700">
                  {notice.message} Check your inbox (and spam folder).
                </p>
              </div>
            </div>
          )}

          {notice?.type === 'error' && (
            <div
              className="mb-6 rounded-xl border border-rose-200 bg-rose-50/80 px-4 py-3 text-sm text-rose-900"
              data-testid="login-error-notice"
              role="alert"
            >
              {notice.message}
            </div>
          )}

          <div className="space-y-4 mb-6">
            <GoogleSignInButton dataTestId="login-google-button" label="Continue with Google" />
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-stone-200" />
              <span className="text-xs uppercase tracking-wider text-stone-400">or</span>
              <div className="h-px flex-1 bg-stone-200" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-white/60 border-stone-200 focus:border-stone-400 focus:ring-4 focus:ring-stone-100 rounded-xl"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-stone-500 hover:text-stone-700"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-white/60 border-stone-200 focus:border-stone-400 focus:ring-4 focus:ring-stone-100 rounded-xl"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || notice?.type === 'locked_out'}
              className="w-full rounded-full px-8 py-6 text-lg bg-stone-800 text-stone-50 hover:bg-stone-700 shadow-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Come back in'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-stone-600">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="text-stone-800 font-medium hover:underline">
              Create one
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
